import React from 'react';
import { styled } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Colors } from 'library';

type LinkSize = 'sm' | 'md' | 'lg';
type LinkWeight = 'medium' | 'semibold';

type LinkBaseProps = {
    children: React.ReactNode;
    size?: LinkSize;
    weight?: LinkWeight;
    underline?: boolean;
    color?: string;
    icon?: React.ReactNode;
    wrap?: boolean;
    className?: string;
};

type LinkInternalProps = LinkBaseProps & { to: string; state?: unknown; href?: never; onClick?: never };
type LinkExternalProps = LinkBaseProps & { href: string; to?: never; onClick?: never };
type LinkButtonProps = LinkBaseProps & { onClick: (e: React.MouseEvent) => void; to?: never; href?: never };
type LinkProps = LinkInternalProps | LinkExternalProps | LinkButtonProps;

const SIZE_PX: Record<LinkSize, number> = { sm: 13, md: 14, lg: 18 };
const WEIGHT_VALUE: Record<LinkWeight, number> = { medium: 500, semibold: 600 };

export function Link({
    children,
    size = 'md',
    weight = 'semibold',
    underline = true,
    color,
    icon,
    wrap = false,
    className,
    ...rest
}: LinkProps) {
    const content = wrap ? (
        <>
            {icon && <IconSlot>{icon}</IconSlot>}
            <TextSlot>{children}</TextSlot>
        </>
    ) : (
        <>
            {icon}
            {children}
        </>
    );

    if ('href' in rest && rest.href) {
        return (
            <ExternalLink
                href={rest.href}
                $size={size}
                $weight={weight}
                $underline={underline}
                $color={color}
                $wrap={wrap}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
            >
                {content}
            </ExternalLink>
        );
    }
    if ('onClick' in rest && rest.onClick) {
        return (
            <ButtonLink
                type="button"
                onClick={rest.onClick}
                $size={size}
                $weight={weight}
                $underline={underline}
                $color={color}
                $wrap={wrap}
                className={className}
            >
                {content}
            </ButtonLink>
        );
    }
    const internalProps = rest as LinkInternalProps;
    const linkTo = internalProps.state ? { pathname: internalProps.to, state: internalProps.state } : internalProps.to;
    return (
        <InternalLink
            to={linkTo}
            $size={size}
            $weight={weight}
            $underline={underline}
            $color={color}
            $wrap={wrap}
            className={className}
        >
            {content}
        </InternalLink>
    );
}

type LinkStyleProps = {
    $size: LinkSize;
    $weight: LinkWeight;
    $underline: boolean;
    $color?: string;
    $wrap: boolean;
};

const linkStyles = ({ $size, $weight, $underline, $color, $wrap }: LinkStyleProps) => {
    const resolvedColor = $color ?? Colors.darkNavy;
    return {
        display: ($wrap ? 'flex' : 'inline-flex') as 'flex' | 'inline-flex',
        alignItems: ($wrap ? 'flex-start' : 'center') as 'flex-start' | 'center',
        gap: 4,
        minWidth: 0,
        fontSize: SIZE_PX[$size],
        fontWeight: WEIGHT_VALUE[$weight],
        color: resolvedColor,
        textDecoration: $underline ? ('underline' as const) : ('none' as const),
        '&:hover': { color: resolvedColor, opacity: 0.7 },
    };
};

const IconSlot = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
});

const TextSlot = styled('span')({
    minWidth: 0,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
});

const shouldForwardLinkProp = (prop: string) => !['$size', '$weight', '$underline', '$color', '$wrap'].includes(prop);

const InternalLink = styled(RouterLink, {
    shouldForwardProp: (prop) => shouldForwardLinkProp(prop as string),
})<LinkStyleProps>(linkStyles);

const ExternalLink = styled('a', {
    shouldForwardProp: (prop) => shouldForwardLinkProp(prop as string),
})<LinkStyleProps>(linkStyles);

const ButtonLink = styled('button', {
    shouldForwardProp: (prop) => shouldForwardLinkProp(prop as string),
})<LinkStyleProps>(linkStyles, {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:focus, &:focus-visible': { outline: 'none' },
});
